import React from 'react';
import {
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { ProjectSchemeInterface, ProjectSchemeType, ProjectSchemePhase } from 'ts-interfaces';

interface ProjectDetailsPanelProps {
  selectedProject: ProjectSchemeInterface;
  editedProject: ProjectSchemeInterface;
  isEditing: boolean;
  isSaving: boolean;
  hasViewerAccess: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onInputChange: (field: string, value: any) => void;
}

export const ProjectDetailsPanel = ({
  selectedProject,
  editedProject,
  isEditing,
  isSaving,
  hasViewerAccess,
  onEdit,
  onSave,
  onCancel,
  onInputChange
}: ProjectDetailsPanelProps) => {
  return (
    <Paper sx={{ p: 2 }}>
      <Grid sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'medium' }}>
          Project Details
        </Typography>
        {!hasViewerAccess &&
          <>
          {!isEditing ? (
            <IconButton aria-label="edit project details" onClick={onEdit} color="primary">
              <EditIcon />
            </IconButton>
          ) : (
            <Stack direction="row" spacing={1}>
              <IconButton aria-label="save project details" onClick={onSave} color="primary" disabled={isSaving}>
                <SaveIcon />
              </IconButton>
              <IconButton onClick={onCancel} color="primary">
                <CancelIcon />
              </IconButton>
            </Stack>
          )}
          </>
        }
      </Grid>
      
      <Grid container spacing={0.2}>
        <Grid item xs={6} sx={{ mt: 1.2 }}>
          <Typography color="text.secondary" variant="body2" noWrap>Project Name</Typography>
        </Grid>
        <Grid item xs={6} sx={{ mt: 1.2 }}>
          {isEditing ? (
            <TextField 
              fullWidth
              size="small"
              value={editedProject.name || ''}
              onChange={(e) => onInputChange('name', e.target.value)}
            />
          ) : (
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{selectedProject.name}</Typography>
          )}
        </Grid>
        <Grid item xs={12}><Divider /></Grid>
        
        <Grid item xs={6} sx={{ mt: 1.2 }}>
          <Typography color="text.secondary" variant="body2" noWrap>Project Type</Typography>
        </Grid>
        <Grid item xs={6} sx={{ mt: 1.2 }}>
          {isEditing ? (
            <FormControl fullWidth size="small">
              <Select
                value={editedProject.type || ''}
                onChange={(e) => onInputChange('type', e.target.value)}
              >
                {Object.values(ProjectSchemeType).map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{selectedProject.type}</Typography>
          )}
        </Grid>
        
        <Grid item xs={12}><Divider sx={{ mb: 0 }} /></Grid>
        
        <Grid item xs={6} sx={{ mt: 1.2 }}>
          <Typography color="text.secondary" variant="body2" noWrap>Project Phase</Typography>
        </Grid>
        <Grid item xs={6} sx={{ mt: 1.2 }}>
          {isEditing ? (
            <FormControl fullWidth size="small">
              <Select
                value={editedProject.phase || ''}
                onChange={(e) => onInputChange('phase', e.target.value)}
              >
                {Object.values(ProjectSchemePhase).map((phase) => (
                  <MenuItem key={phase} value={phase}>{phase}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{selectedProject.phase}</Typography>
          )}
        </Grid>
        
        <Grid item xs={12}><Divider sx={{ mb: 0 }} /></Grid>
        
        <Grid item xs={6} sx={{ mt: 1.2 }}>
          <Typography color="text.secondary" variant="body2" noWrap>Target Date</Typography>
        </Grid>
        <Grid item xs={6} sx={{ mt: 1.2 }}>
          {isEditing ? (
            <TextField 
              fullWidth
              size="small"
              type="date"
              value={editedProject.startDate || ''}
              onChange={(e) => onInputChange('startDate', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          ) : (
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{selectedProject.startDate}</Typography>
          )}
        </Grid>
        
        <Grid item xs={12}><Divider sx={{ mb: 0 }} /></Grid>
        
        <Grid item xs={6} sx={{ mt: 1.2 }}>
          <Typography color="text.secondary" variant="body2" noWrap>Project Location</Typography>
        </Grid>
        <Grid item xs={6} sx={{ mt: 1.2 }}>
          {isEditing ? (
            <TextField 
              fullWidth
              size="small"
              value={editedProject.location || ''}
              onChange={(e) => onInputChange('location', e.target.value)}
            />
          ) : (
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{selectedProject.location}</Typography>
          )}
        </Grid>
        
        <Grid item xs={12}><Divider sx={{ mb: 0 }} /></Grid>
        
        <Grid item xs={12} sx={{ mt: 1.2 }}>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5 }} noWrap>Description</Typography>
          {isEditing ? (
            <TextField 
              fullWidth
              multiline
              rows={4}
              value={editedProject.description || ''}
              onChange={(e) => onInputChange('description', e.target.value)}
              placeholder="Enter project description..."
            />
          ) : (
            <Typography 
              variant="body2" 
              color="text.primary" 
              sx={{ 
                fontSize: '0.875rem',
                lineHeight: 1.5,
                wordWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                maxHeight: '150px',
                overflowY: 'auto',
                fontStyle: selectedProject.description ? 'normal' : 'italic'
              }}
            >
              {selectedProject.description}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};